"use client";

import { useEffect, useState, useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { LayoutGrid, TriangleAlert } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import BankTransferInfo from "@/components/section-registration/bank-transfer-info";
import RegistrationSteps from "@/components/section-registration/registration-steps";
import { EventInfoBlock } from "@/components/section-events/event-info-block";
import { EventLocationBlock } from "@/components/section-events/event-location-block";
import { registerForEvent, unregisterFromEvent } from "@/query/registrations";
import { formatDateTime } from "@/lib/format";
import type { Event, EventRegistration, EventLocation } from "@/lib/types";

interface RegistrationWizardProps {
  event: Omit<Event, "join_password">;
  requiresPassword: boolean;
  registration: EventRegistration | null;
  isLoggedIn: boolean;
  isEventPast: boolean;
  isUnderage: boolean;
  paypalEnabled: boolean;
  eventFee: number;
  bankAccountHolder: string;
  bankIban: string;
  bankName: string;
  reference: string;
  eventInfoContent: string | null;
  eventLocation: EventLocation | null;
}

export default function RegistrationWizard({
  event,
  requiresPassword,
  registration,
  isLoggedIn,
  isEventPast,
  isUnderage,
  paypalEnabled,
  eventFee,
  bankAccountHolder,
  bankIban,
  bankName,
  reference,
  eventInfoContent,
  eventLocation,
}: RegistrationWizardProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [joinPassword, setJoinPassword] = useState("");

  useEffect(() => {
    const paypalStatus = searchParams.get("paypal");
    const token = searchParams.get("token");
    if (!paypalStatus) return;

    if (paypalStatus === "success" && token) {
      fetch("/api/paypal/event/capture-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId: token, eventId: event.event_id }),
      })
        .then((r) => r.json() as Promise<{ success?: boolean; alreadyCaptured?: boolean; error?: string }>)
        .then((data) => {
          if (data.success) {
            toast.success(data.alreadyCaptured ? "Zahlung bereits verbucht." : "Zahlung erfolgreich!");
            router.refresh();
          } else {
            toast.error(data.error ?? "Fehler beim Abschließen der Zahlung.");
          }
        })
        .catch(() => toast.error("Fehler beim Abschließen der Zahlung."));
    } else if (paypalStatus === "cancel") {
      toast.info("PayPal-Zahlung abgebrochen.");
    }

    router.replace(`/home/events/${event.event_id}/anmelden`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleRegister = () => {
    startTransition(async () => {
      const result = await registerForEvent(event.event_id, joinPassword);
      if (result.success) {
        toast.success("Anmeldung erfolgreich!");
        router.refresh();
      } else {
        const messages: Record<string, string> = {
          already_registered: "Du bist bereits angemeldet.",
          event_full: "Dieses Event ist bereits ausgebucht.",
          registration_closed: "Die Anmeldung für dieses Event ist geschlossen.",
          event_past: "Dieses Event liegt in der Vergangenheit.",
          wrong_password: "Falsches Passwort.",
          under_age: "Du musst zum Eventbeginn mindestens 18 Jahre alt sein, um dich anzumelden.",
        };
        toast.error(messages[result.reason ?? ""] ?? "Fehler bei der Anmeldung.");
      }
    });
  };

  const handleUnregister = () => {
    startTransition(async () => {
      const result = await unregisterFromEvent(event.event_id);
      if (result.success) {
        toast.success("Anmeldung storniert.");
        router.refresh();
      } else if (result.reason === "already_paid") {
        toast.error("Stornieren nicht möglich: Du hast bereits bezahlt.");
      } else {
        toast.error("Stornieren fehlgeschlagen.");
      }
    });
  };

  const handlePaypal = async () => {
    setPaypalLoading(true);
    try {
      const res = await fetch("/api/paypal/event/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventId: event.event_id }),
      });
      const data = (await res.json()) as { approvalUrl?: string; error?: string };
      if (!res.ok || !data.approvalUrl) {
        toast.error(data.error ?? "PayPal-Fehler.");
        return;
      }
      window.location.href = data.approvalUrl;
    } catch {
      toast.error("PayPal nicht erreichbar.");
    } finally {
      setPaypalLoading(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          <a href="/login" className="underline hover:text-foreground">
            Melde dich an
          </a>
          , um dich für dieses Event anzumelden.
        </CardContent>
      </Card>
    );
  }

  // Zustand: keine Registrierung vorhanden
  if (!registration) {
    if (isEventPast || !event.is_registration_open) {
      return (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Anmeldung nicht möglich</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{isEventPast ? "Dieses Event liegt in der Vergangenheit. Eine Anmeldung ist daher nicht mehr möglich." : "Die Anmeldung für dieses Event ist derzeit geschlossen."}</p>
            </CardContent>
          </Card>
          {!requiresPassword && (
            <div className="grid gap-4 sm:grid-cols-2">
              <EventInfoBlock content={eventInfoContent} />
              <EventLocationBlock location={eventLocation} />
            </div>
          )}
        </div>
      );
    }

    // While a password is required, nothing about the event is revealed in this step at
    // all — a correct password immediately completes registration and moves the wizard to
    // step 2, where the info blocks are already shown unconditionally, so there's no
    // legitimate state where the password is unverified but the blocks should be visible.
    const unlocked = !requiresPassword;

    return (
      <div className="space-y-4">
        <RegistrationSteps currentStep={1} />
        <Card>
          <CardHeader>
            <CardTitle>{event.event_name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {unlocked && (
              <>
                <p className="text-sm text-muted-foreground">
                  {formatDateTime(event.event_date_start)} – {formatDateTime(event.event_date_end)}
                  {event.location ? ` · ${event.location}` : ""}
                </p>
                <p className="text-sm">
                  Teilnahmegebühr: <span className="font-semibold">{eventFee.toFixed(2).replace(".", ",")} €</span>
                </p>
              </>
            )}
            {requiresPassword && (
              <div className="max-w-xs">
                <Label htmlFor="join-password" className="text-xs text-muted-foreground">
                  Event-Passwort
                </Label>
                <Input
                  id="join-password"
                  type="password"
                  value={joinPassword}
                  onChange={(e) => setJoinPassword(e.target.value)}
                  className="mt-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRegister();
                  }}
                  autoFocus
                />
              </div>
            )}
            {isUnderage ? (
              <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
                <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Du musst zum Eventbeginn mindestens 18 Jahre alt sein, um dich anzumelden.</span>
              </div>
            ) : (
              <Button onClick={handleRegister} disabled={isPending || (requiresPassword && !joinPassword.trim())}>
                {isPending ? "Wird angemeldet…" : "Jetzt anmelden"}
              </Button>
            )}
          </CardContent>
        </Card>
        {unlocked && (
          <div className="grid gap-4 sm:grid-cols-2">
            <EventInfoBlock content={eventInfoContent} />
            <EventLocationBlock location={eventLocation} />
          </div>
        )}
      </div>
    );
  }

  // Zustand: registriert, noch nicht bezahlt
  if (!registration.has_paid) {
    return (
      <div className="space-y-4">
        <RegistrationSteps currentStep={2} />
        <Card>
          <CardHeader>
            <CardTitle>Zahlung ausstehend</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">Du bist für dieses Event angemeldet. Um teilzunehmen, bezahle bitte die Teilnahmegebühr.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              {paypalEnabled && (
                <div className="rounded-lg border p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold">PayPal</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Direkt online bezahlen, sofort bestätigt.</p>
                  </div>
                  <Button onClick={handlePaypal} disabled={paypalLoading} className="bg-[#0070ba] hover:bg-[#005ea6] text-white gap-2">
                    {paypalLoading ? (
                      "Weiterleiten…"
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c1.57 2.126.977 5.556-1.74 7.25-1.594.992-3.599 1.45-5.942 1.45h-.758l-1.154 7.328H8.54l-.272 1.727h3.795c.459 0 .85-.334.921-.787l.038-.196.732-4.638.047-.256a.932.932 0 0 1 .921-.787h.58c3.758 0 6.7-1.527 7.558-5.944.362-1.86.175-3.413-.638-4.607z" />
                        </svg>
                        PayPal
                      </>
                    )}
                  </Button>
                </div>
              )}
              <BankTransferInfo fee={eventFee} accountHolder={bankAccountHolder} iban={bankIban} bankName={bankName} reference={reference} eventId={event.event_id} />
            </div>
            <ConfirmDialog
              trigger={
                <Button variant="destructive" disabled={isPending}>
                  Anmeldung stornieren
                </Button>
              }
              title="Anmeldung stornieren?"
              description="Deine Anmeldung für dieses Event wird vollständig gelöscht. Um dich erneut anzumelden, musst du den Anmeldeprozess wieder von vorne durchlaufen."
              confirmLabel="Stornieren"
              onConfirm={handleUnregister}
            />
          </CardContent>
        </Card>
        <div className="grid gap-4 sm:grid-cols-2">
          <EventInfoBlock content={eventInfoContent} />
          <EventLocationBlock location={eventLocation} />
        </div>
      </div>
    );
  }

  // Zustand: bezahlt, aber noch kein Sitzplatz gewählt
  if (!registration.seat_id) {
    return (
      <div className="space-y-4">
        <RegistrationSteps currentStep={3} />
        <Card>
          <CardHeader>
            <CardTitle>Wähle deinen Sitzplatz</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button asChild className="gap-2">
              <a href={`/home/events/${event.event_id}/sitzplan`}>
                <LayoutGrid className="h-4 w-4" />
                Sitzplatz wählen
              </a>
            </Button>
            <p className="text-sm text-muted-foreground">Du bist bezahlt angemeldet. Wähle jetzt deinen Sitzplatz im Sitzplan.</p>
          </CardContent>
        </Card>
        <div className="grid gap-4 sm:grid-cols-2">
          <EventInfoBlock content={eventInfoContent} />
          <EventLocationBlock location={eventLocation} />
        </div>
      </div>
    );
  }

  // Zustand: bezahlt und Sitzplatz gewählt
  return (
    <div className="space-y-4">
      <RegistrationSteps currentStep={4} />
      <Card>
        <CardHeader>
          <CardTitle>Für dieses Event bist du angemeldet :)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button asChild variant="outline" className="gap-2">
            <a href={`/home/events/${event.event_id}/sitzplan`}>
              <LayoutGrid className="h-4 w-4" />
              Zum Sitzplan
            </a>
          </Button>
          <p className="text-sm text-muted-foreground">
            Dein Sitzplatz: <span className="font-semibold text-foreground">{registration.seat_number}</span>
          </p>
        </CardContent>
      </Card>
      <div className="grid gap-4 sm:grid-cols-2">
        <EventInfoBlock content={eventInfoContent} />
        <EventLocationBlock location={eventLocation} />
      </div>
    </div>
  );
}
